import { html } from "lit";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../Button/index.js";
import "../Link/index.js";
import "../Badge/index.js";
import "../../atom/Column/index.js";
import "../../atom/Row/index.js";
import "../../atom/Text/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { rif } from "../../../helpers/renderIf.js";
import { isMobile } from "../../../responsive/index.js";
import { fileSizeInMbFormatted, timestampFormatted } from "../FileCard/utils.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfFileManager = class extends SignalWatcher(BaseComponent) {
  constructor() {
    super(...arguments);
    this.dsf_file_size = 0;
    this.dsf_locale = navigator.language;
    this.dsf_status = "review";
    this.dsf_status_label = "";
  }
  handleLinkClick() {
    this.emit("file-click");
  }
  handleDeleteClick() {
    this.emit("delete-click");
  }
  renderFileInfoComponent() {
    return html`
      <div class="flex flex-col gap-4">
        <div class="flex items-baseline gap-8">
          <dsf-link
            class="w-fit"
            dsf_size="medium"
            dsf_href=${ifDefined(this.dsf_file_url)}
            @click=${this.handleLinkClick}
            >${this.dsf_file_name}</dsf-link
          >
          ${rif(
      this.dsf_file_size,
      html`<span class="dsf-text-xxs text-neutral-300"
              >${fileSizeInMbFormatted(this.dsf_file_size)}</span
            >`
    )}
        </div>
        ${rif(
      this.dsf_message,
      html`<div class="flex items-center gap-4 text-nowrap">
            <span class="dsf-text-xxs text-neutral-400">${this.dsf_message}</span>
            <span class="dsf-text-xxs text-neutral-300"
              >${timestampFormatted(this.dsf_timestamp, this.dsf_locale)}</span
            >
          </div>`
    )}
      </div>
    `;
  }
  renderBadgeComponent() {
    let badgeLevel = "info";
    if (this.dsf_status === "approved") {
      badgeLevel = "success";
    } else if (this.dsf_status === "rejected") {
      badgeLevel = "critical";
    }
    return html`
      <dsf-badge dsf_level=${badgeLevel} class="whitespace-nowrap">
        ${this.dsf_status_label}
      </dsf-badge>
    `;
  }
  renderReasonComponent() {
    const desktopClasses = "flex flex-auto flex-col align-middle h-full";
    const mobileClasses = "flex flex-auto flex-col items-start";
    return html`
      ${rif(
      this.dsf_reason_title || this.dsf_reason_message,
      html`
          <div class=${isMobile() ? mobileClasses : desktopClasses}>
            ${rif(
        this.dsf_reason_title,
        html`
                <dsf-text dsf_level=${isMobile() ? "xs-semibold" : "xxs-semibold"}
                  >${this.dsf_reason_title}</dsf-text
                >
              `
      )}
            ${rif(
        this.dsf_reason_message,
        html`
                <dsf-text dsf_level=${isMobile() ? "xs" : "xxs"}
                  >${this.dsf_reason_message}</dsf-text
                >
              `
      )}
          </div>
        `
    )}
    `;
  }
  renderDeleteButton() {
    return html`
      <dsf-button
        dsf_square
        dsf_level="secondary"
        dsf_icon_left="Trash2"
        dsf_size="small"
        dsf_accessibility_label=${ifDefined(this.dsf_delete_accessibility_label)}
        @click=${this.handleDeleteClick}
      >
      </dsf-button>
    `;
  }
  renderMobile() {
    return html`<div
      class="flex h-fit flex-auto flex-col gap-12 rounded-m border border-neutral-100 p-12"
    >
      <dsf-row dsf_gap="xxl" class="w-full justify-between">
        ${this.renderFileInfoComponent()} ${this.renderBadgeComponent()}
        ${this.renderDeleteButton()}
      </dsf-row>
      ${this.renderReasonComponent()}
      <div class="flex w-full">
        <slot name="actions"></slot>
      </div>
    </div>`;
  }
  renderDesktop() {
    return html`<div
      class="flex min-h-64 flex-auto items-center gap-52 rounded-m border border-neutral-100 p-12"
    >
      ${this.renderFileInfoComponent()} ${this.renderBadgeComponent()}
      ${this.renderReasonComponent()}
      <div class="ml-auto flex gap-8">
        <slot name="actions"></slot>
        ${this.renderDeleteButton()}
      </div>
    </div>`;
  }
  render() {
    if (isMobile()) {
      return this.renderMobile();
    }
    return this.renderDesktop();
  }
};
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_file_name", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_file_url", 2);
__decorateClass([
  prop({ type: Number })
], DsfFileManager.prototype, "dsf_file_size", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_timestamp", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfFileManager.prototype, "dsf_status", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_status_label", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_reason_title", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_reason_message", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_delete_accessibility_label", 2);
DsfFileManager = __decorateClass([
  defineElement("dsf-file-manager")
], DsfFileManager);
export {
  DsfFileManager
};
